import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody, CardHeader, Table, Input, Button, Row, Col, Spinner, Alert, Collapse } from 'reactstrap';
import { comptabiliteActions } from '../../sagas/comptabiliteSlice';

const GrandLivre = () => {
    const dispatch = useDispatch();
    const { grandLivre, loading, error } = useSelector(state => state.comptabilite);
    
    const [filters, setFilters] = useState({
        dateDebut: '',
        dateFin: '',
        classeCompte: ''
    });

    const [expandedAccounts, setExpandedAccounts] = useState({});

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSearch = () => {
        dispatch(comptabiliteActions.getGrandLivre({
            dateDebut: filters.dateDebut || null,
            dateFin: filters.dateFin || null,
            classeCompte: filters.classeCompte || null
        }));
    };

    const toggleAccount = (numeroCompte) => {
        setExpandedAccounts(prev => ({
            ...prev,
            [numeroCompte]: !prev[numeroCompte]
        }));
    };

    const formatPrice = (amount) => {
        return new Intl.NumberFormat('fr-DZ', {
            style: 'currency',
            currency: 'DZD'
        }).format(amount || 0);
    };

    const exportToPDF = () => {
        // Implementation for PDF export
        console.log('Export to PDF functionality to be implemented');
    };

    return (
        <div className="page-content">
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="card-title mb-0">Grand Livre</h4>
                        <Button color="danger" onClick={exportToPDF}>
                            Exporter PDF
                        </Button>
                    </div>
                </CardHeader>
                <CardBody>
                    {/* Filters */}
                    <Row className="mb-3">
                        <Col md={3}>
                            <Input
                                type="date"
                                placeholder="Date début"
                                value={filters.dateDebut}
                                onChange={(e) => handleFilterChange('dateDebut', e.target.value)}
                            />
                        </Col>
                        <Col md={3}>
                            <Input
                                type="date"
                                placeholder="Date fin"
                                value={filters.dateFin}
                                onChange={(e) => handleFilterChange('dateFin', e.target.value)}
                            />
                        </Col>
                        <Col md={3}>
                            <Input
                                type="select"
                                value={filters.classeCompte}
                                onChange={(e) => handleFilterChange('classeCompte', e.target.value)}
                            >
                                <option value="">Toutes les classes</option>
                                <option value="1">Classe 1 - Comptes de capitaux</option>
                                <option value="2">Classe 2 - Comptes d'immobilisations</option>
                                <option value="3">Classe 3 - Comptes de stocks</option>
                                <option value="4">Classe 4 - Comptes de tiers</option>
                                <option value="5">Classe 5 - Comptes financiers</option>
                                <option value="6">Classe 6 - Comptes de charges</option>
                                <option value="7">Classe 7 - Comptes de produits</option>
                            </Input>
                        </Col>
                        <Col md={3}>
                            <Button 
                                color="primary" 
                                onClick={handleSearch}
                                disabled={loading.grandLivre}
                            >
                                {loading.grandLivre ? <Spinner size="sm" /> : 'Générer'}
                            </Button>
                        </Col>
                    </Row>

                    {/* Error Display */}
                    {error && (
                        <Alert color="danger">
                            {error}
                        </Alert>
                    )}

                    {/* Loading */}
                    {loading.grandLivre && (
                        <div className="text-center">
                            <Spinner color="primary" />
                        </div>
                    )}

                    {/* Grand Livre Display */}
                    {grandLivre && Object.keys(grandLivre).length > 0 && (
                        <div className="grand-livre-container">
                            {Object.entries(grandLivre).map(([numeroCompte, releve]) => (
                                <Card key={numeroCompte} className="mb-3">
                                    <CardHeader 
                                        className="cursor-pointer"
                                        onClick={() => toggleAccount(numeroCompte)}
                                    >
                                        <Row className="align-items-center">
                                            <Col md={8}>
                                                <h6 className="mb-0">
                                                    <i className={`mdi mdi-chevron-${expandedAccounts[numeroCompte] ? 'down' : 'right'} me-2`}></i>
                                                    <strong>{numeroCompte}</strong> - {releve.intituleCompte}
                                                </h6>
                                                <small className="text-muted">{releve.typeCompte}</small>
                                            </Col>
                                            <Col md={4} className="text-end">
                                                <div className="d-flex justify-content-end">
                                                    <div className="me-3">
                                                        <small className="text-muted d-block">Solde Initial</small>
                                                        <strong>{formatPrice(releve.soldeInitial)}</strong>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted d-block">Solde Actuel</small>
                                                        <strong className={releve.soldeActuel >= 0 ? 'text-success' : 'text-danger'}>
                                                            {formatPrice(releve.soldeActuel)}
                                                        </strong>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardHeader>
                                    
                                    <Collapse isOpen={expandedAccounts[numeroCompte]}>
                                        <CardBody>
                                            {/* Account Summary */}
                                            <Row className="mb-3">
                                                <Col md={4} className="text-center">
                                                    <div className="border rounded p-2">
                                                        <small className="text-muted d-block">Total Débit</small>
                                                        <strong>{formatPrice(releve.totalDebit)}</strong>
                                                    </div>
                                                </Col>
                                                <Col md={4} className="text-center">
                                                    <div className="border rounded p-2">
                                                        <small className="text-muted d-block">Total Crédit</small>
                                                        <strong>{formatPrice(releve.totalCredit)}</strong>
                                                    </div>
                                                </Col>
                                                <Col md={4} className="text-center">
                                                    <div className="border rounded p-2">
                                                        <small className="text-muted d-block">Mouvement Net</small>
                                                        <strong className={(releve.totalDebit - releve.totalCredit) >= 0 ? 'text-success' : 'text-danger'}>
                                                            {formatPrice(releve.totalDebit - releve.totalCredit)}
                                                        </strong>
                                                    </div>
                                                </Col>
                                            </Row>

                                            {/* Account Entries */}
                                            {releve.ecritures && releve.ecritures.length > 0 ? (
                                                <Table responsive size="sm">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>N° Écriture</th>
                                                            <th>Libellé</th>
                                                            <th>Journal</th>
                                                            <th className="text-end">Débit</th>
                                                            <th className="text-end">Crédit</th>
                                                            <th className="text-end">Solde</th>
                                                            <th>Ref</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {/* Initial Balance Row */}
                                                        <tr className="table-info">
                                                            <td colSpan="6"><strong>Solde à l'ouverture</strong></td>
                                                            <td className="text-end">
                                                                <strong>{formatPrice(releve.soldeInitial)}</strong>
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                        
                                                        {/* Entries */}
                                                        {releve.ecritures.map((ecriture, index) => {
                                                            // Calculate running balance
                                                            const runningBalance = releve.soldeInitial + 
                                                                releve.ecritures.slice(0, index + 1)
                                                                    .reduce((sum, e) => sum + (e.montantDebit || 0) - (e.montantCredit || 0), 0);
                                                            
                                                            return (
                                                                <tr key={index}>
                                                                    <td>{new Date(ecriture.dateEcriture).toLocaleDateString('fr-DZ')}</td>
                                                                    <td>{ecriture.numeroEcriture}</td>
                                                                    <td>{ecriture.libelle}</td>
                                                                    <td>{ecriture.journalLibelle}</td>
                                                                    <td className="text-end">
                                                                        {ecriture.montantDebit ? formatPrice(ecriture.montantDebit) : '-'}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {ecriture.montantCredit ? formatPrice(ecriture.montantCredit) : '-'}
                                                                    </td>
                                                                    <td className={`text-end ${runningBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                        {formatPrice(runningBalance)}
                                                                    </td>
                                                                    <td>{ecriture.referenceDocument}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                        
                                                        {/* Final Balance Row */}
                                                        <tr className="table-warning">
                                                            <td colSpan="6"><strong>Solde à la clôture</strong></td>
                                                            <td className="text-end">
                                                                <strong className={releve.soldeActuel >= 0 ? 'text-success' : 'text-danger'}>
                                                                    {formatPrice(releve.soldeActuel)}
                                                                </strong>
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            ) : (
                                                <Alert color="info">
                                                    Aucune écriture pour cette période
                                                </Alert>
                                            )}
                                        </CardBody>
                                    </Collapse>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {grandLivre && Object.keys(grandLivre).length === 0 && !loading.grandLivre && (
                        <div className="text-center py-4">
                            <h5>Aucune donnée disponible</h5>
                            <p className="text-muted">Aucun compte trouvé pour les critères sélectionnés.</p>
                        </div>
                    )}
                </CardBody>
            </Card>

            <style jsx>{`
                .cursor-pointer {
                    cursor: pointer;
                }
                .grand-livre-container .card-header:hover {
                    background-color: #f8f9fa;
                }
            `}</style>
        </div>
    );
};

export default GrandLivre;
